import React, { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiService } from '../../services/api'
import toast from 'react-hot-toast'
import LoadingSpinner from '../common/LoadingSpinner'
import { I, Icon } from '../shared/ns-icons'
import { NSSelect } from '../shared/ns-kit'
import '../../styles/admin-pages.css'

// ── Formatters ────────────────────────────────────────────────────────────────
const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(amount ?? 0)

const formatDate = (dateString) =>
  new Date(dateString).toLocaleDateString('en-GB', { year: 'numeric', month: 'short', day: 'numeric' })

const gradIndex = (id) =>
  ((id || '').toString().split('').reduce((a, c) => a + c.charCodeAt(0), 0) % 6) + 1

const getInitials = (name = '') =>
  name.split(' ').map(w => w[0]).slice(0, 2).join('').toUpperCase() || '?'

// ── Triangle alert icon (no equivalent in shared set) ──────────────────────────
function IconAlert({ size = 16 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
      <line x1="12" y1="9" x2="12" y2="13" /><line x1="12" y1="17" x2="12.01" y2="17" />
    </svg>
  )
}

// ── Revenue area chart (pure SVG) ──────────────────────────────────────────────
function RevenueAreaChart({ data = [], height = 240, color = 'var(--accent)' }) {
  if (!data.length) return <div style={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-faint)', fontSize: 12 }}>No data</div>

  const values = data.map(d => typeof d === 'number' ? d : (d.value || 0))
  const max = Math.max(...values, 1)
  const w = 500, h = height - 24

  const pts = values.map((v, i) => {
    const x = values.length > 1 ? (i / (values.length - 1)) * w : w / 2
    const y = h - (v / max) * (h - 12) - 4
    return `${x},${y}`
  })
  const areaPath = `M ${pts.join(' L ')} L ${w},${h} L 0,${h} Z`
  const linePath = `M ${pts.join(' L ')}`
  const xLabels = data.map(d => typeof d === 'object' ? d.name : '').filter(Boolean)

  return (
    <div>
      <svg viewBox={`0 0 ${w} ${h}`} preserveAspectRatio="none" style={{ width: '100%', height }}>
        <defs>
          <linearGradient id="revGrad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor={color} stopOpacity={0.28} />
            <stop offset="100%" stopColor={color} stopOpacity={0} />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#revGrad)" />
        <path d={linePath} fill="none" stroke={color} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {values.map((v, i) => {
          const x = values.length > 1 ? (i / (values.length - 1)) * w : w / 2
          const y = h - (v / max) * (h - 12) - 4
          return <circle key={i} cx={x} cy={y} r="3.5" fill={color} stroke="var(--surface)" strokeWidth="2" />
        })}
      </svg>
      {xLabels.length > 0 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 11, color: 'var(--text-faint)', marginTop: 8, fontFamily: 'var(--font-mono)' }}>
          {xLabels.filter((_, i) => i % Math.ceil(xLabels.length / 5) === 0 || i === xLabels.length - 1).map((l, i) => (
            <span key={i}>{l}</span>
          ))}
        </div>
      )}
    </div>
  )
}

// ── Plan donut chart ───────────────────────────────────────────────────────────
function PlanDonut({ data = [], size = 130, thickness = 16 }) {
  const total = data.reduce((s, d) => s + (d.count || 0), 0)
  if (!total) return null
  const r = (size - thickness) / 2
  const cx = size / 2, cy = size / 2
  const circ = 2 * Math.PI * r
  let offset = 0

  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`} style={{ flexShrink: 0 }}>
      {data.map((seg, i) => {
        const pct = seg.count / total
        const dash = pct * circ
        const el = (
          <circle
            key={i}
            cx={cx} cy={cy} r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth={thickness}
            strokeDasharray={`${dash} ${circ - dash}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${cx} ${cy})`}
          />
        )
        offset += dash
        return el
      })}
      <text x={cx} y={cy - size * 0.04} textAnchor="middle" fontSize={size * 0.14} fontWeight="700" fill="var(--text)">{total}</text>
      <text x={cx} y={cy + size * 0.1} textAnchor="middle" fontSize={size * 0.08} fill="var(--text-faint)">active</text>
    </svg>
  )
}

// ── Mini stat (prototype BillingStat) ──────────────────────────────────────────
function BillingStat({ label, value, sub, delta }) {
  return (
    <div className="card card-pad-sm">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6 }}>
        <div style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</div>
        {delta && (
          <span className={delta === 'up' ? 'delta-up' : 'delta-down'}>
            <I.arrowUp style={{ fontSize: 10, transform: delta === 'down' ? 'rotate(180deg)' : 'none' }} />
          </span>
        )}
      </div>
      <div style={{ fontSize: 24, fontWeight: 600, letterSpacing: '-0.02em', fontFamily: 'var(--font-mono)' }}>{value}</div>
      <div style={{ fontSize: 11.5, color: 'var(--text-muted)', marginTop: 4 }}>{sub}</div>
    </div>
  )
}

// ── Status / plan tones ────────────────────────────────────────────────────────
const STATUS_TONE = { paid: 'success', active: 'success', pending: 'info', failed: 'danger', overdue: 'warning', cancelled: 'danger' }
const STATUS_LABEL = { paid: 'Paid', active: 'Active', pending: 'Pending', failed: 'Failed', overdue: 'Overdue', cancelled: 'Cancelled' }
const PLAN_TONE = { basic: 'success', pro: 'info', enterprise: 'warning' }

// ── Main component ─────────────────────────────────────────────────────────────
const BillingDashboard = () => {
  const [selectedMetric, setSelectedMetric] = useState('revenue')
  const [range, setRange] = useState('90d')
  const [dismissedAlert, setDismissedAlert] = useState(false)
  const [attentionOnly, setAttentionOnly] = useState(false)
  const queryClient = useQueryClient()

  // ── Queries ───────────────────────────────────────────────────────────────
  const { data: billingData, isLoading: billingLoading, error: billingError, refetch: refetchBilling } = useQuery(
    'billing-overview',
    () => apiService.get('/billing/overview').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  const { data: overagesData, isLoading: overagesLoading, error: overagesError } = useQuery(
    'billing-overages',
    () => apiService.get('/billing/overages').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  const { data: plansData, error: plansError } = useQuery(
    'billing-plans',
    () => apiService.get('/billing/plans').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  const { data: historicalData, error: historicalError } = useQuery(
    'billing-historical',
    () => apiService.get('/billing/historical').then(res => res.data),
    { retry: 1, staleTime: 10 * 60 * 1000 }
  )

  const { data: growthData, error: growthError } = useQuery(
    'billing-growth',
    () => apiService.get('/billing/growth').then(res => res.data),
    { retry: 1, staleTime: 5 * 60 * 1000 }
  )

  // ── Mutations ─────────────────────────────────────────────────────────────
  const updateSummaryMutation = useMutation(
    () => apiService.post('/billing/update-summary'),
    {
      onSuccess: () => {
        toast.success('Billing summary updated successfully')
        queryClient.invalidateQueries('billing-overview')
        queryClient.invalidateQueries('billing-overages')
        queryClient.invalidateQueries('billing-historical')
        queryClient.invalidateQueries('billing-growth')
      },
      onError: () => toast.error('Failed to update billing summary')
    }
  )

  const hasErrors = billingError || overagesError || plansError || historicalError || growthError
  const isLoading = billingLoading || overagesLoading

  const chartData = historicalData || [
    { name: 'Jan', revenue: 0, clients: 0, overages: 0 },
    { name: 'Feb', revenue: 0, clients: 0, overages: 0 },
    { name: 'Mar', revenue: 0, clients: 0, overages: 0 },
    { name: 'Apr', revenue: 0, clients: 0, overages: 0 },
    { name: 'May', revenue: 0, clients: 0, overages: 0 },
    { name: 'Jun', revenue: 0, clients: 0, overages: 0 },
  ]

  const growthMetrics = growthData || {
    totalRevenue:         { current: 0, previous: 0, growth: 0 },
    baseRevenue:          { current: 0, previous: 0, growth: 0 },
    overageRevenue:       { current: 0, previous: 0, growth: 0 },
    activeSubscriptions:  { current: 0, previous: 0, growth: 0 },
    avgRevenuePerClient:  { current: 0, previous: 0, growth: 0 },
  }

  if (isLoading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: 240 }}>
        <LoadingSpinner size="lg" text="Loading billing data..." />
      </div>
    )
  }

  const displayBillingData = billingData || {
    totalRevenue: 0, baseRevenue: 0, overageRevenue: 0, activeSubscriptions: 0,
    planBreakdown: [], clientBilling: [], lastUpdated: new Date().toISOString()
  }
  const displayOveragesData = overagesData || { totalOverageRevenue: 0, overageCount: 0, overages: [] }

  const totalRevenue        = displayBillingData.totalRevenue || 0
  const overageRevenue      = displayBillingData.overageRevenue || 0
  const activeSubscriptions = displayBillingData.activeSubscriptions || 0
  const planBreakdown       = displayBillingData.planBreakdown || []
  const clientBilling       = displayBillingData.clientBilling || []

  const failedPayments = clientBilling.filter(c => c.status === 'failed' || c.status === 'overdue')
  const PLAN_COLORS = { basic: '#22c55e', pro: '#6c63ff', enterprise: '#f59e0b' }

  const planDonutData = planBreakdown.map(p => ({
    name: plansData?.[p.plan_id]?.name || p.plan_id,
    count: p.count || 0,
    price: plansData?.[p.plan_id]?.price || 0,
    color: PLAN_COLORS[p.plan_id] || '#a3a3a3',
    plan_id: p.plan_id,
  }))

  // Donut scales down as more plan rows appear, leaving room for the text underneath.
  const donutSize = Math.max(120, 230 - (planDonutData.length - 1) * 32)
  const donutThickness = Math.round(donutSize * 0.13)

  const visibleClients = attentionOnly
    ? clientBilling.filter(c => c.status === 'failed' || c.status === 'overdue')
    : clientBilling

  // ── Revenue trend window (tabs) + period delta ─────────────────────────────
  // Source data is monthly (Jan…Jun); each tab slices a trailing window and the
  // delta reflects the change across that visible window.
  const RANGE_MONTHS = { '30d': 2, '90d': 3, 'YTD': chartData.length }
  const windowSize = Math.min(RANGE_MONTHS[range] || chartData.length, chartData.length)
  const chartWindow = chartData.slice(-Math.max(windowSize, 2))
  const windowVals = chartWindow.map(d => d[selectedMetric] || 0)
  const isCurrencyMetric = selectedMetric !== 'clients'
  // Headline: total over the window for money metrics; latest count for clients.
  const chartHeadline = isCurrencyMetric
    ? formatCurrency(windowVals.reduce((s, v) => s + v, 0))
    : (windowVals[windowVals.length - 1] || 0).toLocaleString()
  // Delta: first → last point of the visible window.
  const firstVal = windowVals[0] || 0
  const lastVal = windowVals[windowVals.length - 1] || 0
  const rangeDelta = firstVal === 0
    ? (lastVal > 0 ? 100 : 0)
    : Math.round(((lastVal - firstVal) / firstVal) * 1000) / 10
  const rangeDeltaUp = rangeDelta >= 0

  // ── Export client billing → CSV ────────────────────────────────────────────
  const exportCsv = () => {
    if (!clientBilling.length) { toast.error('No billing data to export'); return }
    const head = ['Client', 'Plan', 'Status', 'Plan cost', 'Overages', 'Total']
    const rows = clientBilling.map(c => [
      c.companyName || '',
      c.planName || c.plan || '',
      c.status || '',
      c.planCost ?? 0,
      c.overage?.hasOverage ? c.overage.overageCost ?? 0 : 0,
      c.totalCost ?? 0,
    ])
    const csv = [head, ...rows].map(r => r.map(v => `"${String(v).replace(/"/g, '""')}"`).join(',')).join('\n')
    const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv' }))
    const a = document.createElement('a')
    a.href = url
    a.download = `billing-${new Date().toISOString().slice(0, 10)}.csv`
    a.click()
    URL.revokeObjectURL(url)
    toast.success('Exported billing CSV')
  }

  const COLS = '1.9fr 0.9fr 0.9fr 1fr 1.1fr 0.9fr auto'

  return (
    <>
      {/* ── Header ─── */}
      <div className="page-header">
        <div className="page-title">
          <div className="eyebrow">Workspace · Billing</div>
          <h1>Billing</h1>
          <div className="subtitle">Revenue, invoices and subscription health</div>
        </div>
        <div className="flex gap-2 items-center">
          <button className="btn" onClick={exportCsv}><I.upload /> Export</button>
          <button className="btn btn-primary" onClick={() => updateSummaryMutation.mutate()} disabled={updateSummaryMutation.isLoading}>
            <I.bolt /> {updateSummaryMutation.isLoading ? 'Updating…' : 'Update summary'}
          </button>
        </div>
      </div>

      <div className="page-body">
        {/* ── Failed payments alert ─── */}
        {failedPayments.length > 0 && !dismissedAlert && (
          <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 12, borderColor: 'var(--danger)', background: 'var(--danger-soft, var(--bg-2))' }}>
            <span className="icon-tile danger" style={{ flexShrink: 0 }}><IconAlert /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13.5, fontWeight: 600, color: 'var(--text)' }}>
                {failedPayments.length} failed payment{failedPayments.length > 1 ? 's' : ''} need attention
              </div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
                {failedPayments.map(c => c.companyName).join(', ')}
              </div>
            </div>
            <button className="site-action" onClick={() => setDismissedAlert(true)} title="Dismiss"><I.x /></button>
          </div>
        )}

        {/* ── Error banner ─── */}
        {hasErrors && (
          <div className="card card-pad" style={{ display: 'flex', alignItems: 'center', gap: 12, borderColor: 'var(--warning)' }}>
            <span className="icon-tile warning" style={{ flexShrink: 0 }}><IconAlert /></span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: 'var(--text)' }}>Unable to load live billing data</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>Showing partial or cached data.</div>
            </div>
            <button
              className="btn btn-sm"
              onClick={() => {
                refetchBilling()
                queryClient.invalidateQueries('billing-overages')
                queryClient.invalidateQueries('billing-plans')
                queryClient.invalidateQueries('billing-historical')
                queryClient.invalidateQueries('billing-growth')
              }}
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Stat row ─── */}
        <div className="bento stagger-in" style={{ gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
          <BillingStat
            label="MRR"
            value={formatCurrency(totalRevenue)}
            sub={`${growthMetrics.totalRevenue.growth >= 0 ? '+' : ''}${growthMetrics.totalRevenue.growth}% vs last month`}
            delta={growthMetrics.totalRevenue.growth >= 0 ? 'up' : 'down'}
          />
          <BillingStat
            label="Active subscriptions"
            value={activeSubscriptions.toLocaleString()}
            sub={`${growthMetrics.activeSubscriptions.growth >= 0 ? '+' : ''}${growthMetrics.activeSubscriptions.growth}% vs last month`}
            delta={growthMetrics.activeSubscriptions.growth >= 0 ? 'up' : 'down'}
          />
          <BillingStat
            label="Overage revenue"
            value={formatCurrency(overageRevenue)}
            sub={`${growthMetrics.overageRevenue.growth >= 0 ? '+' : ''}${growthMetrics.overageRevenue.growth}% vs last month`}
            delta={growthMetrics.overageRevenue.growth >= 0 ? 'up' : 'down'}
          />
          <BillingStat
            label="Avg revenue / client"
            value={formatCurrency(growthMetrics.avgRevenuePerClient.current)}
            sub={`${growthMetrics.avgRevenuePerClient.growth >= 0 ? '+' : ''}${growthMetrics.avgRevenuePerClient.growth}% vs last month`}
            delta={growthMetrics.avgRevenuePerClient.growth >= 0 ? 'up' : 'down'}
          />
        </div>

        {/* ── Chart row ─── */}
        <div className="bento" style={{ gridTemplateColumns: '2fr 1fr', gap: 14, alignItems: 'stretch' }}>

          {/* Revenue trend */}
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Revenue trend</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Rolling monthly, GBP</div>
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <div className="tabs">
                  {['30d', '90d', 'YTD'].map(r => (
                    <button key={r} className={range === r ? 'active' : ''} onClick={() => setRange(r)}>{r}</button>
                  ))}
                </div>
                <NSSelect
                  value={selectedMetric}
                  onChange={e => setSelectedMetric(e.target.value)}
                  style={{ width: 130, flexShrink: 0 }}
                  options={[
                    { value: 'revenue', label: 'Revenue' },
                    { value: 'clients', label: 'Clients' },
                    { value: 'overages', label: 'Overages' },
                  ]}
                />
              </div>
            </div>
            <div className="stat-row" style={{ marginBottom: 14 }}>
              <div className="num">{chartHeadline}</div>
              <div className={rangeDeltaUp ? 'delta-up' : 'delta-down'}>{rangeDeltaUp ? '+' : ''}{rangeDelta}%</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)', marginLeft: 8 }}>vs period start</div>
            </div>
            <RevenueAreaChart data={chartWindow.map(d => ({ name: d.name, value: d[selectedMetric] || 0 }))} height={240} color="var(--accent)" />
          </div>

          {/* Plan distribution */}
          <div className="card card-pad">
            <h2 className="h2">Plan distribution</h2>
            <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4, marginBottom: 14 }}>By active subscribers</div>

            {planDonutData.length > 0 ? (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 18 }}>
                <PlanDonut data={planDonutData} size={donutSize} thickness={donutThickness} />
                <div style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {planDonutData.map((p, i) => (
                    <div key={i} style={{ fontSize: 12.5 }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                        <span style={{ width: 8, height: 8, borderRadius: 3, background: p.color }} />
                        <span style={{ color: 'var(--text-soft)' }}>{p.name}</span>
                        <span style={{ marginLeft: 'auto', fontFamily: 'var(--font-mono)', color: 'var(--text)' }}>{p.count}</span>
                      </div>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontFamily: 'var(--font-mono)', marginLeft: 14 }}>
                        {formatCurrency(p.price)} × {p.count} = {formatCurrency(p.price * p.count)}/mo
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', padding: '28px 0', color: 'var(--text-faint)', gap: 8 }}>
                <Icon name="spark" style={{ fontSize: 30 }} />
                <div style={{ fontSize: 13 }}>No active subscriptions</div>
              </div>
            )}
          </div>
        </div>

        {/* ── Client billing table ─── */}
        <div className="card card-pad">
          <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
            <div>
              <h2 className="h2">Client billing</h2>
              <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>{clientBilling.length} clients · {failedPayments.length} need attention</div>
            </div>
            <button
              className={`btn btn-sm${attentionOnly ? ' btn-primary' : ''}`}
              onClick={() => setAttentionOnly(v => !v)}
              title="Show only clients needing attention"
            >
              <I.filter /> {attentionOnly ? 'Needs attention' : 'Filter'}
            </button>
          </div>
          <div className="sites-table">
            <div className="row header" style={{ gridTemplateColumns: COLS }}>
              <div>Client</div><div>Plan</div><div>Status</div><div>Plan cost</div><div>Overages</div><div>Total</div><div></div>
            </div>
            {visibleClients.length > 0 ? visibleClients.map((client, i) => {
              const status = client.status || 'paid'
              return (
                <div className="row" key={client.clientId || i} style={{ gridTemplateColumns: COLS }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
                    <div className={`avatar avatar-grad-${gradIndex(client.clientId || client.companyName)}`} style={{ width: 26, height: 26, fontSize: 10 }}>
                      {getInitials(client.companyName)}
                    </div>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{client.companyName}</div>
                      <div style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>ID: {String(client.clientId || '').slice(0, 8)}…</div>
                    </div>
                  </div>
                  <div>
                    <span className={`badge badge-${PLAN_TONE[client.plan] || 'info'}`}>
                      {client.planName || plansData?.[client.plan]?.name || client.plan}
                    </span>
                  </div>
                  <div>
                    <span className={`badge badge-${STATUS_TONE[status] || 'info'}`}><span className="badge-dot" /> {STATUS_LABEL[status] || status}</span>
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 500 }}>{formatCurrency(client.planCost)}</div>
                  <div>
                    {client.overage?.hasOverage ? (
                      <>
                        <div style={{ fontSize: 12.5, fontWeight: 600, color: 'var(--warning)' }}>+{client.overage.overage} edits</div>
                        <div style={{ fontSize: 11, color: 'var(--text-faint)', fontFamily: 'var(--font-mono)' }}>{formatCurrency(client.overage.overageCost)}</div>
                      </>
                    ) : (
                      <span style={{ fontSize: 12, color: 'var(--text-faint)' }}>None</span>
                    )}
                  </div>
                  <div style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, fontSize: 13.5 }}>{formatCurrency(client.totalCost)}</div>
                  <div style={{ display: 'flex', gap: 4 }}>
                    <button className="site-action" title="View client"><I.eye /></button>
                    <button className="site-action" title="More"><I.more /></button>
                  </div>
                </div>
              )
            }) : (
              <div className="row" style={{ gridTemplateColumns: '1fr' }}>
                <div style={{ textAlign: 'center', padding: '28px 0', color: 'var(--text-faint)', fontSize: 13 }}>
                  {attentionOnly ? 'No clients need attention' : 'No billing data available for this period'}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* ── Usage overages ─── */}
        {displayOveragesData.overages?.length > 0 && (
          <div className="card card-pad">
            <div className="flex items-center justify-between" style={{ marginBottom: 14 }}>
              <div>
                <h2 className="h2">Usage overages</h2>
                <div className="text-muted" style={{ fontSize: 12.5, marginTop: 4 }}>Clients over their plan limits this month</div>
              </div>
              {displayOveragesData.overageCount > 0 && (
                <span className="badge badge-warning"><span className="badge-dot" /> {displayOveragesData.overageCount} clients</span>
              )}
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {displayOveragesData.overages.slice(0, 5).map((o, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 11, padding: '12px 16px' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <span className="icon-tile warning" style={{ flexShrink: 0 }}><IconAlert /></span>
                    <div>
                      <div style={{ fontWeight: 500, fontSize: 13 }}>{o.companyName}</div>
                      <div style={{ fontSize: 11.5, color: 'var(--text-muted)' }}>{o.used} / {o.quota} edits (+{o.overage} over)</div>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <div style={{ fontSize: 15, fontWeight: 700, color: 'var(--warning)', fontFamily: 'var(--font-mono)' }}>{formatCurrency(o.overageCost)}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-faint)' }}>overage fee</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── Last updated ─── */}
        {displayBillingData.lastUpdated && (
          <div style={{ textAlign: 'center' }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 12, color: 'var(--text-faint)', background: 'var(--bg-2)', border: '1px solid var(--border)', borderRadius: 999, padding: '5px 14px' }}>
              <Icon name="clock" /> Last updated: {formatDate(displayBillingData.lastUpdated)}
            </span>
          </div>
        )}

      </div>
    </>
  )
}

export default BillingDashboard
